import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Send, Shield, Download, Bell, XCircle, CheckCircle, Clock, User } from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-600',
  sent: 'bg-blue-50 text-blue-700',
  viewed: 'bg-purple-50 text-purple-700',
  completed: 'bg-emerald-50 text-emerald-700',
  declined: 'bg-red-50 text-red-700',
  voided: 'bg-slate-100 text-slate-400',
};

const RECIPIENT_STATUS = {
  pending: { color: 'text-slate-500', icon: Clock },
  viewed: { color: 'text-blue-600', icon: User },
  signed: { color: 'text-emerald-600', icon: CheckCircle },
  declined: { color: 'text-red-600', icon: XCircle },
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function EnvelopeDetail() {
  const { envelopeId } = useParams();
  const navigate = useNavigate();
  const [env, setEnv] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      API.get(`/envelopes/${envelopeId}`),
      API.get(`/audit-logs/envelope/${envelopeId}`),
    ]).then(([envResp, logsResp]) => {
      setEnv(envResp.data);
      setAuditLogs(logsResp.data);
    }).catch(() => toast.error('Failed to load envelope'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [envelopeId]);

  const handleVoid = async () => {
    if (!window.confirm('Void this envelope? Signers will no longer be able to sign.')) return;
    try {
      await API.post(`/envelopes/${envelopeId}/void`);
      toast.success('Envelope voided');
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleRemind = async () => {
    try {
      const r = await API.post(`/envelopes/${envelopeId}/remind`);
      toast.success(r.data.message);
    } catch (err) { toast.error(formatError(err)); }
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>;
  if (!env) return <div className="p-8 text-center text-slate-400">Envelope not found</div>;

  const signedCount = (env.recipients || []).filter(r => r.status === 'signed').length;
  const totalSigners = (env.recipients || []).filter(r => r.role !== 'cc').length;

  return (
    <div className="p-6 md:p-8 fade-in max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/envelopes')} className="p-1.5 text-slate-400 hover:text-slate-700 rounded" data-testid="back-btn">
            <ChevronLeft size={18} />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>{env.title}</h1>
            <p className="text-sm text-slate-500 mt-0.5">{env.document_name}</p>
          </div>
        </div>
        <span className={`text-xs px-2.5 py-1.5 rounded-sm font-semibold uppercase tracking-wide ${STATUS_STYLES[env.status] || 'bg-slate-100 text-slate-600'}`}
          data-testid="envelope-status-badge">
          {env.status}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 space-y-5">
          {/* Progress */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Signing Progress</h3>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">{signedCount} of {totalSigners} signed</span>
              <span className="text-sm font-semibold text-slate-900">{totalSigners > 0 ? Math.round((signedCount / totalSigners) * 100) : 0}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${totalSigners > 0 ? (signedCount / totalSigners) * 100 : 0}%` }} />
            </div>
          </div>

          {/* Recipients */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Recipients</h3>
            <div className="space-y-3">
              {(env.recipients || []).map(r => {
                const rStatus = RECIPIENT_STATUS[r.status] || RECIPIENT_STATUS.pending;
                const Icon = rStatus.icon;
                return (
                  <div key={r.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`recipient-${r.id}`}>
                    <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-slate-600">{r.name[0]?.toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{r.name}</p>
                      <p className="text-xs text-slate-400">{r.email} · <span className="capitalize">{r.role}</span> · Order {r.order}</p>
                    </div>
                    <div className={`flex items-center gap-1.5 text-xs font-medium ${rStatus.color}`}>
                      <Icon size={13} />
                      <span className="capitalize">{r.status}</span>
                    </div>
                    {r.signed_at && <span className="text-xs text-slate-400 hidden sm:block">{formatDate(r.signed_at)}</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Audit Log */}
          {auditLogs.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Audit Trail</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {auditLogs.map(log => (
                  <div key={log.id} className="flex items-start gap-3 text-xs py-1.5 border-b border-slate-50 last:border-0">
                    <span className="text-slate-400 flex-shrink-0 w-32">{formatDate(log.timestamp)}</span>
                    <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono flex-shrink-0">{log.event}</span>
                    <span className="text-slate-500">from {log.ip}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Actions */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Actions</h3>
            <div className="space-y-2">
              {env.status === 'completed' && env.certificate_id && (
                <Link
                  to={`/verify/${env.certificate_id}`}
                  target="_blank"
                  className="w-full flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-sm text-sm font-medium transition-colors"
                  data-testid="view-certificate-btn"
                >
                  <Shield size={15} /> View Certificate
                </Link>
              )}
              {['sent', 'viewed'].includes(env.status) && (
                <button onClick={handleRemind} className="w-full flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-sm text-sm font-medium transition-colors" data-testid="remind-btn">
                  <Bell size={15} /> Send Reminder
                </button>
              )}
              {!['completed', 'voided', 'declined'].includes(env.status) && (
                <button onClick={handleVoid} className="w-full flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-sm text-sm font-medium transition-colors" data-testid="void-btn">
                  <XCircle size={15} /> Void Envelope
                </button>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Details</h3>
            <dl className="space-y-2 text-sm">
              {[
                ['Workflow', env.workflow],
                ['Created', formatDate(env.created_at)],
                ['Expires', formatDate(env.expires_at)],
                ['Completed', formatDate(env.completed_at)],
              ].map(([label, value]) => value && value !== '—' && (
                <div key={label} className="flex items-start">
                  <dt className="text-slate-500 w-24 flex-shrink-0">{label}</dt>
                  <dd className="text-slate-900 capitalize">{value}</dd>
                </div>
              ))}
              {env.hash && (
                <div className="flex items-start">
                  <dt className="text-slate-500 w-24 flex-shrink-0">Hash</dt>
                  <dd className="text-slate-600 font-mono text-xs break-all">{env.hash.substring(0, 16)}...</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Signing links */}
          {['sent', 'viewed'].includes(env.status) && (
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Signing Links</h3>
              <div className="space-y-2">
                {(env.recipients || []).filter(r => r.status !== 'signed' && r.role !== 'cc').map(r => (
                  <div key={r.id} className="text-xs">
                    <p className="text-slate-600 font-medium mb-1">{r.name}</p>
                    <a
                      href={`${window.location.origin}/sign/${r.token}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline break-all"
                      data-testid={`signing-link-${r.id}`}
                    >
                      Open signing link
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
